import React, { useState, useLayoutEffect, useRef } from "react";
import { Box, Typography, InputBase } from "@mui/material";
import { toPrice } from "../../../../../../helpers";

interface EditablePercentFeeRowProps {
  labelPrefix: string;
  labelSuffix: string;
  percentValue: number;
  displayValue: string;
  onPercentChange: (value: number) => void;
}

export default function EditablePercentFeeRow({
  labelPrefix,
  labelSuffix,
  percentValue,
  displayValue,
  onPercentChange,
}: EditablePercentFeeRowProps) {
  const [isEditingPercent, setIsEditingPercent] = useState(false);
  const [inputWidth, setInputWidth] = useState(0);
  const measureRef = useRef<HTMLSpanElement>(null);

  // Calculate width based on content - use useLayoutEffect to prevent flicker
  useLayoutEffect(() => {
    if (measureRef.current) {
      const textWidth = measureRef.current.offsetWidth;
      // Add a bit more padding for better fit
      setInputWidth(textWidth);
    }
  }, [percentValue]);

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 0.125,
        minHeight: "20px",
        pr: "20px",
      }}
    >
      <Typography
        component="span"
        fontSize="12px"
        fontWeight={400}
        color="#555555"
        lineHeight="18px"
      >
        {labelPrefix}
        {!isEditingPercent && (
          <Typography
            component="span"
            fontSize="12px"
            fontWeight={400}
            color="#555555"
            lineHeight="18px"
            sx={{
              cursor: "pointer",
              textDecorationLine: "underline",
              textDecorationStyle: "dotted",
              textDecorationColor: "#555555",
              textUnderlineOffset: "2px",
            }}
            onClick={() => setIsEditingPercent(true)}
          >
            {toPrice(percentValue)}
          </Typography>
        )}
        {isEditingPercent && (
          <>
            <InputBase
              type="number"
              value={percentValue}
              onChange={(event) => {
                onPercentChange(toPrice(event.target.value));
              }}
              onBlur={() => {
                setIsEditingPercent(false);
              }}
              onFocus={(event) => event.target.select()}
              slotProps={{
                input: {
                  step: 0.01,
                  min: 0,
                  max: 100,
                },
              }}
              sx={{
                width: `${inputWidth}px`,
                display: "inline-flex",
                fontSize: "12px",
                lineHeight: "18px",
                fontWeight: 400,
                color: "#555555",
                verticalAlign: "baseline",
                "& input": {
                  textAlign: "left",
                  padding: "0",
                  fontSize: "12px",
                  fontWeight: 400,
                  fontFamily: "inherit",
                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                    {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  MozAppearance: "textfield",
                },
              }}
              autoFocus
            />
          </>
        )}
        {labelSuffix}
      </Typography>

      {/* Hidden span to measure text width - must match input styling exactly */}
      <span
        ref={measureRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "pre",
          fontSize: "12px",
          fontWeight: 400,
          fontFamily: "inherit",
          padding: 0,
          margin: 0,
          border: 0,
        }}
      >
        {percentValue}
      </span>

      <Typography
        fontSize="12px"
        fontWeight={400}
        color="#555555"
        lineHeight="18px"
      >
        {displayValue}
      </Typography>
    </Box>
  );
}
